import { generateVideoThumbnails } from "@rajesh896/video-thumbnails-generator";
import { uploadFileToS3 } from "api/media-library/upload-file-to-s3";
import { PostMediaFile } from "shared/types/post";
import {
  logUploadBreadcrumb,
  withUploadSpan,
} from "api/media-library/upload-observability";
import { resolveMediaCorrelationKey } from "utils/media-observability";

export interface VideoUploadProgress {
  phase: "thumbnail" | "video" | "complete";
  progress: number;
}

export interface VideoUploadResult {
  videoUrl: string;
  thumbnailUrl: string;
}

/**
 * Upload edited video with thumbnail generation
 */
export async function uploadEditedVideo({
  videoBlob,
  previousFile,
  onProgress,
}: {
  videoBlob: Blob;
  previousFile: PostMediaFile;
  onProgress?: (progress: VideoUploadProgress) => void;
}): Promise<PostMediaFile> {
  const metadata = {
    fileName: previousFile.fileName || "edited-video.mp4",
    previousUrl: previousFile.url,
    previousThumbnailUrl: previousFile.thumbnailUrl,
    blobSize: videoBlob.size,
    mediaCorrelationKey: resolveMediaCorrelationKey({
      assetId: previousFile.asset_id,
      urls: [
        previousFile.url,
        previousFile.originalUrl,
        previousFile.thumbnailUrl,
      ],
      fileName: previousFile.fileName,
    }),
  };

  try {
    return withUploadSpan("media_upload.edited_video", metadata, async () => {
      logUploadBreadcrumb("media_upload.edited_video.start", metadata);
      onProgress?.({ phase: "thumbnail", progress: 0 });

      let thumbnailUrl = previousFile.thumbnailUrl;

      try {
        const videoFile = new File(
          [videoBlob],
          previousFile.fileName || "video.mp4",
          {
            type: "video/mp4",
          }
        );

        const thumbnails = await withUploadSpan(
          "media_upload.edited_video.generate_thumbnail",
          metadata,
          async () => generateVideoThumbnails(videoFile, 1, "jpg")
        );

        if (thumbnails && thumbnails.length > 0) {
          const response = await fetch(thumbnails[0]);
          const thumbnailBlob = await response.blob();

          onProgress?.({ phase: "thumbnail", progress: 50 });

          const uploadedThumbnailUrl = await uploadFileToS3({
            blob: thumbnailBlob,
            fileName: `${previousFile.fileName || "video"}-thumb-${Date.now()}.jpg`,
          });

          thumbnailUrl = uploadedThumbnailUrl;
          logUploadBreadcrumb("media_upload.edited_video.thumbnail.success", {
            ...metadata,
            thumbnailUrl,
          });
        }
      } catch (thumbnailError) {
        console.warn(
          "Thumbnail generation failed, using original thumbnail:",
          thumbnailError
        );
        logUploadBreadcrumb("media_upload.edited_video.thumbnail.failure", {
          ...metadata,
          errorMessage:
            thumbnailError instanceof Error
              ? thumbnailError.message
              : String(thumbnailError),
        });
      }

      onProgress?.({ phase: "video", progress: 0 });

      const videoUrl = await uploadFileToS3(
        {
          blob: videoBlob,
          fileName: previousFile.fileName || "edited-video.mp4",
        },
        {
          onProgress: (progress) => {
            onProgress?.({ phase: "video", progress });
          },
        }
      );

      onProgress?.({ phase: "complete", progress: 100 });
      logUploadBreadcrumb("media_upload.edited_video.complete", {
        ...metadata,
        videoUrl,
        thumbnailUrl,
      });

      return {
        originalUrl: previousFile.originalUrl || previousFile.url,
        altText: previousFile.altText,
        fileName: previousFile.fileName || "edited-video.mp4",
        isVideo: true,
        thumbnailUrl,
        url: videoUrl,
        type: "video/mp4",
        asset_id: previousFile.asset_id,
        meta: {
          ...previousFile.meta,
          size: videoBlob.size / 1024,
          isVideo: true,
        },
      };
    });
  } catch (error) {
    console.error("Video upload failed:", error);
    throw error;
  }
}
